import { useQuery } from '@tanstack/react-query'
import { History } from 'lucide-react'
import type { ReactNode } from 'react'

import { parseAmount } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import { formatDocumentNumber } from '@aeris/printing'
import { Badge, LoadFailed, Spinner } from '@aeris/ui'

import { fetchFinanceEntries, fetchSalesDocuments } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { formatDate } from '../lib/date-format.ts'

/**
 * O que a pessoa já fez com a loja — F132.
 *
 * *"Esse cliente compra sempre?"*, *"ele está devendo?"*: as duas perguntas de
 * quem abre a ficha no balcão. A API já listava vendas por cliente e títulos
 * por pessoa; a ficha só mostrava marcadores, crédito, vale e endereços.
 *
 * Cada metade aparece para quem pode ler aquilo — um vendedor sem
 * `finance.read` vê as compras e não os títulos, e nenhum 403 vai para o
 * console.
 */

const LIMIT = 5

export const PartyHistory = ({ partyId }: { partyId: string }): ReactNode => {
  const { allows } = useAuth()
  const canReadSales = allows('sales.read')
  const canReadFinance = allows('finance.read')

  const sales = useQuery({
    queryKey: ['party-history', 'sales', partyId],
    queryFn: ({ signal }) =>
      fetchSalesDocuments(
        {
          kind: 'sale',
          customerPartyId: partyId,
          page: 1,
          pageSize: LIMIT,
          sort: 'issued_at',
          direction: 'desc',
        },
        signal
      ),
    enabled: canReadSales,
  })

  const open = useQuery({
    queryKey: ['party-history', 'open-entries', partyId],
    queryFn: ({ signal }) =>
      fetchFinanceEntries(
        {
          partyId,
          status: 'open',
          limit: LIMIT,
          sort: 'due_at',
          order: 'asc',
        },
        signal
      ),
    enabled: canReadFinance,
  })

  if (!canReadSales && !canReadFinance) return null

  return (
    <section className="flex flex-col gap-3">
      <h3 className="flex items-center gap-1.5 text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        <History aria-hidden="true" className="h-4 w-4" />
        Histórico
      </h3>

      {canReadSales ? (
        <div className="flex flex-col gap-1">
          <span className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
            {sales.data === undefined
              ? 'Últimas compras'
              : sales.data.total === 0
                ? 'Nenhuma compra ainda'
                : `${String(sales.data.total)} ${sales.data.total === 1 ? 'compra' : 'compras'} · ${formatMoney(parseAmount(sales.data.totalAmount))} no total`}
          </span>
          {sales.isPending ? (
            <Spinner />
          ) : sales.isError ? (
            <LoadFailed
              what="as compras"
              onRetry={() => {
                void sales.refetch()
              }}
            />
          ) : (
            <ul className="flex flex-col divide-y divide-(--border-subtle)">
              {sales.data.items.map((sale) => (
                <li
                  key={sale.id}
                  className="flex items-center justify-between gap-3 py-1.5 text-(length:--text-sm)"
                >
                  <span className="text-(--text-secondary)">
                    {formatDate(sale.issuedAt)} · nº{' '}
                    {formatDocumentNumber(sale.number)}
                    {sale.status === 'cancelled' ? (
                      <Badge tone="neutral" className="ml-2">
                        Cancelada
                      </Badge>
                    ) : null}
                  </span>
                  <span className="font-(--font-mono) text-(--text-primary)">
                    {formatMoney(parseAmount(sale.totalAmount))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {canReadFinance ? (
        <div className="flex flex-col gap-1">
          <span className="text-(length:--text-xs) font-(--weight-medium) text-(--text-secondary)">
            {open.data === undefined
              ? 'Títulos em aberto'
              : open.data.total === 0
                ? 'Nenhum título em aberto'
                : `${String(open.data.total)} em aberto · ${formatMoney(parseAmount(open.data.outstandingTotal))}`}
          </span>
          {open.isPending ? (
            <Spinner />
          ) : open.isError ? (
            <LoadFailed
              what="os títulos"
              onRetry={() => {
                void open.refetch()
              }}
            />
          ) : (
            <ul className="flex flex-col divide-y divide-(--border-subtle)">
              {open.data.items.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-center justify-between gap-3 py-1.5 text-(length:--text-sm)"
                >
                  <span className="text-(--text-secondary)">
                    vence {formatDate(entry.dueAt)} · {entry.description}
                    {entry.isOverdue ? (
                      <Badge tone="danger" className="ml-2">
                        {entry.daysOverdue === 1
                          ? '1 dia atrasado'
                          : `${String(entry.daysOverdue)} dias atrasado`}
                      </Badge>
                    ) : null}
                  </span>
                  <span className="font-(--font-mono) text-(--text-primary)">
                    {formatMoney(parseAmount(entry.outstanding))}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </section>
  )
}
